import React from 'react';

const SettingsSidebar = ({
  activeTab,
  setActiveTab,
  isEditing,
  profileImagePreview,
  profileResponse,
  handleImageClick,
  fileInputRef,
  handleImageChange,
  profileData
}) => {
  const tabs = [
    { id: 'personal', name: 'Personal Info', icon: '👤' },
    { id: 'professional', name: 'Professional', icon: '💼' },
    { id: 'security', name: 'Security', icon: '🔒' }
  ];

  // Function to generate avatar based on name
  const getInitialsAvatar = () => {
    const firstName = profileData?.personal?.firstName || '';
    const lastName = profileData?.personal?.lastName || '';
    
    // Get first letter of first name and last name
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    
    // If both names exist, return both initials, otherwise return first initial or '?'
    if (firstInitial && lastInitial) {
      return `${firstInitial}${lastInitial}`;
    } else if (firstInitial) {
      return firstInitial;
    } else {
      return '?';
    }
  };

  // Function to generate consistent color based on name
  const getAvatarColor = () => {
    const firstName = profileData?.personal?.firstName || '';
    const lastName = profileData?.personal?.lastName || '';
    const name = firstName + lastName;
    
    // Generate a hash from the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // List of gradient colors
    const gradients = [
      'from-green-400 to-green-600',
      'from-blue-400 to-blue-600',
      'from-purple-400 to-purple-600',
      'from-pink-400 to-pink-600',
      'from-yellow-400 to-yellow-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
      'from-teal-400 to-teal-600'
    ];
    
    // Use hash to pick a consistent color
    const index = Math.abs(hash) % gradients.length;
    return gradients[index];
  };

  // Check if image exists
  const hasImage = profileImagePreview || profileResponse?.data?.image;

  return (
    <div className="lg:w-64 bg-gradient-to-b from-green-50 to-white border-r border-green-100 p-6">
      <div className="space-y-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`w-full flex items-center space-x-3 px-4 py-4 rounded-2xl text-left transition-all duration-300 ease-out transform hover:scale-105 ${
              activeTab === tab.id
                ? 'bg-green-500 text-white shadow-lg shadow-green-200 scale-105'
                : 'text-gray-700 hover:bg-green-100 hover:text-green-700'
            }`}
          >
            <span className="text-xl">{tab.icon}</span>
            <span className="font-semibold">{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Profile Image Section */}
      <div className="mt-12 p-6 bg-white rounded-2xl shadow-lg border border-green-100 transform transition-all duration-500 hover:scale-105">
        <div className="text-center">
          <div className="relative inline-block">
            {hasImage ? (
              // Show image if available
              <img
                className="h-24 w-24 rounded-2xl object-cover border-4 border-green-200 shadow-lg transform transition-all duration-500 hover:scale-110 hover:border-green-300"
                src={profileImagePreview || profileResponse?.data?.image}
                alt="Profile"
              />
            ) : (
              // Show avatar with initials if no image
              <div className={`h-24 w-24 rounded-2xl bg-gradient-to-br ${getAvatarColor()} border-4 border-green-200 shadow-lg transform transition-all duration-500 hover:scale-110 hover:border-green-300 flex items-center justify-center`}>
                <span className="text-3xl font-bold text-white">
                  {getInitialsAvatar()}
                </span>
              </div>
            )}
            
            {isEditing && (
              <>
                <button 
                  onClick={handleImageClick}
                  className="absolute -bottom-2 -right-2 bg-green-500 text-white p-2 rounded-full shadow-lg transform transition-all duration-300 hover:scale-110 hover:bg-green-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </>
            )}
          </div>
          <h3 className="mt-4 text-lg font-bold text-gray-900">
            {profileData?.personal?.firstName || ''} {profileData?.personal?.lastName || ''}
          </h3>
          <div className="mt-3 w-12 h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full mx-auto"></div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSidebar;